/**
 * Fill-ups, and what they say about the fuel figures.
 *
 * Everything the app reports about fuel is an estimate: the injector or MAF
 * reading turned into litres per hour. The pump knows the truth. Recording a
 * fill-up gives both a real consumption figure -- litres actually bought over
 * the distance actually driven -- and a correction factor for the estimate,
 * the same way GPS corrects the car's speed reading.
 *
 * Only full-to-full spans give a true figure: a partial fill leaves an unknown
 * amount in the tank. Partial fills between two full ones still count towards
 * the litres burnt in that span, so a top-up on a long trip does no harm.
 */

export interface FuelFill {
  /** Stable id, so an edited or deleted entry can be found again. */
  id: string;
  /** Epoch ms of the fill. */
  t: number;
  litres: number;
  /** Filled right up. Only these close a span. */
  full: boolean;
  pricePerL?: number;
  /** The car's own odometer, if the driver noted it. Display only. */
  odometerKm?: number;
  /** Distance the app measured since the previous fill. */
  distanceKm: number;
  /** What the app thought was burnt over that distance. */
  estimatedL: number;
  note?: string;
}

/** One full-to-full span: the only kind that yields a true figure. */
export interface TankSpan {
  from: FuelFill;
  to: FuelFill;
  /** Litres bought over the span, partial top-ups included. */
  litres: number;
  distanceKm: number;
  /** What the app estimated over the same distance. */
  estimatedL: number;
  /** The real figure, in L/100km. */
  consumption: number;
  /** What the app would have said, in L/100km. */
  estimatedConsumption?: number;
  /** How far the estimate was off, in percent; positive means it read high. */
  errorPct?: number;
  cost?: number;
}

/**
 * How far the app's fuel estimate is from the pump, learned from full-to-full
 * spans. Multiplies the estimated flow, so 1.06 means the car really burns 6%
 * more than the readings suggest.
 */
export interface FuelCalibration {
  factor: number;
  /** Litres actually bought over the spans behind the factor. */
  litres: number;
  estimatedL: number;
  distanceKm: number;
  spans: number;
}

/** A span shorter than this is too small for tank-level noise to average out. */
const MIN_SPAN_KM = 50;
/** Nothing sensible comes from correcting by more than this. */
const MIN_FACTOR = 0.5;
const MAX_FACTOR = 2;
/** Fills further apart than this probably have a missed fill between them. */
const MAX_SPAN_DAYS = 180;

export function sortFills(fills: readonly FuelFill[]): FuelFill[] {
  return [...fills].sort((a, b) => a.t - b.t);
}

/**
 * The full-to-full spans in a list of fills, oldest first.
 *
 * A span runs from one full fill to the next, and the litres counted are those
 * put in *after* the opening fill: that is exactly the fuel burnt between the
 * two moments the tank was known to be full.
 */
export function tankSpans(fills: readonly FuelFill[]): TankSpan[] {
  const sorted = sortFills(fills);
  const spans: TankSpan[] = [];
  let open: FuelFill | undefined;
  let litres = 0;
  let distanceKm = 0;
  let estimatedL = 0;
  let cost = 0;
  let costed = true;

  for (const fill of sorted) {
    if (open) {
      litres += fill.litres;
      distanceKm += fill.distanceKm;
      estimatedL += fill.estimatedL;
      if (fill.pricePerL !== undefined) cost += fill.litres * fill.pricePerL;
      else costed = false;

      if (fill.full) {
        const tooOld = fill.t - open.t > MAX_SPAN_DAYS * 86_400_000;
        if (distanceKm > 0 && litres > 0 && !tooOld) {
          const span: TankSpan = {
            from: open,
            to: fill,
            litres,
            distanceKm,
            estimatedL,
            consumption: (litres / distanceKm) * 100,
          };
          if (estimatedL > 0) {
            span.estimatedConsumption = (estimatedL / distanceKm) * 100;
            span.errorPct = (estimatedL / litres - 1) * 100;
          }
          if (costed && cost > 0) span.cost = cost;
          spans.push(span);
        }
        open = fill;
        litres = 0;
        distanceKm = 0;
        estimatedL = 0;
        cost = 0;
        costed = true;
      }
      continue;
    }
    if (fill.full) open = fill;
  }
  return spans;
}

/**
 * The correction factor from every usable span, pooled.
 *
 * Pooling rather than averaging the per-span factors weights a long span more
 * than a short one, which is right: the error in a span is dominated by how
 * precisely the tank was filled, a fixed amount of litres either way.
 */
export function fuelCalibrationFrom(fills: readonly FuelFill[]): FuelCalibration | undefined {
  let litres = 0;
  let estimatedL = 0;
  let distanceKm = 0;
  let spans = 0;
  for (const span of tankSpans(fills)) {
    if (span.distanceKm < MIN_SPAN_KM || span.estimatedL <= 0) continue;
    litres += span.litres;
    estimatedL += span.estimatedL;
    distanceKm += span.distanceKm;
    spans++;
  }
  if (spans === 0 || estimatedL <= 0) return undefined;
  const factor = litres / estimatedL;
  if (!(factor >= MIN_FACTOR && factor <= MAX_FACTOR)) return undefined;
  return { factor, litres, estimatedL, distanceKm, spans };
}

/** Real consumption over every full-to-full span, in L/100km. */
export function lifetimeConsumption(fills: readonly FuelFill[]): { consumption: number; distanceKm: number; litres: number } | undefined {
  let litres = 0;
  let distanceKm = 0;
  for (const span of tankSpans(fills)) {
    litres += span.litres;
    distanceKm += span.distanceKm;
  }
  if (distanceKm <= 0 || litres <= 0) return undefined;
  return { consumption: (litres / distanceKm) * 100, distanceKm, litres };
}

/** What the fuel has cost, over the fills that recorded a price. */
export function fuelSpend(fills: readonly FuelFill[]): { total: number; litres: number; perLitre: number } | undefined {
  let total = 0;
  let litres = 0;
  for (const f of fills) {
    if (f.pricePerL === undefined || !(f.pricePerL > 0)) continue;
    total += f.litres * f.pricePerL;
    litres += f.litres;
  }
  return litres > 0 ? { total, litres, perLitre: total / litres } : undefined;
}

export function sanitizeCalibration(raw: unknown): FuelCalibration | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const factor = numOr(r.factor, NaN);
  if (!(factor >= MIN_FACTOR && factor <= MAX_FACTOR)) return undefined;
  return {
    factor,
    litres: Math.max(0, numOr(r.litres, 0)),
    estimatedL: Math.max(0, numOr(r.estimatedL, 0)),
    distanceKm: Math.max(0, numOr(r.distanceKm, 0)),
    spans: Math.max(0, Math.round(numOr(r.spans, 0))),
  };
}

/** Clean one fill-up from storage or a file; undefined if it is not one. */
export function sanitizeFill(raw: unknown): FuelFill | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const litres = numOr(r.litres, NaN);
  const t = numOr(r.t, NaN);
  // A tanker is not a car: anything past a few hundred litres is a typo.
  if (!(litres > 0 && litres <= 400) || !(t > 0)) return undefined;
  const fill: FuelFill = {
    id: typeof r.id === 'string' && r.id.length > 0 && r.id.length <= 64 ? r.id : 'fill-' + Math.round(t),
    t,
    litres,
    full: r.full !== false,
    distanceKm: Math.max(0, numOr(r.distanceKm, 0)),
    estimatedL: Math.max(0, numOr(r.estimatedL, 0)),
  };
  const price = numOr(r.pricePerL, NaN);
  if (price > 0 && price < 100) fill.pricePerL = price;
  const odo = numOr(r.odometerKm, NaN);
  if (odo > 0 && odo < 10_000_000) fill.odometerKm = odo;
  if (typeof r.note === 'string' && r.note.trim().length > 0) fill.note = r.note.slice(0, 200);
  return fill;
}

const numOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;
